import { useState } from "react";
import { Download, Mail, KeyRound, Building2, RefreshCw } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import client, { apiError } from "../lib/api";
import { SOCIAL } from "../data/content";

function ScreenerForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState({ type: "idle", text: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await client.post("/screener-request", { ...form, topic: "licensing" });
      setStatus({ type: "ok", text: "Screener request received — Providence Film Group will reply with access details." });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus({ type: "err", text: apiError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="screener-request-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Your name" aria-label="Your name" data-testid="screener-name"
          className="rounded-xl border border-white/15 bg-black/40 px-5 py-3.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Work email" aria-label="Work email" data-testid="screener-email"
          className="rounded-xl border border-white/15 bg-black/40 px-5 py-3.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
      </div>
      <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
        placeholder="Company, territory, and what you're interested in (optional)" rows={3} aria-label="Message" data-testid="screener-message"
        className="w-full rounded-xl border border-white/15 bg-black/40 px-5 py-3.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
      <button type="submit" disabled={loading} data-testid="screener-submit"
        className="rounded-full bg-[#E11D48] px-8 py-3.5 font-semibold text-white transition-colors hover:bg-[#F43F5E] disabled:opacity-60">
        {loading ? "Sending…" : "Request Screener Access"}
      </button>
      {status.type !== "idle" && (
        <p role="status" className={`text-sm ${status.type === "ok" ? "text-emerald-400" : "text-red-400"}`} data-testid="screener-status">{status.text}</p>
      )}
    </form>
  );
}

export default function Industry() {
  return (
    <main className="pt-16" data-testid="industry-page">
      <Seo title="Licensing & Partnerships" description="License the original Laugh at L.A. season or commission Providence Film Group for a new edition or local adaptation." />
      <section className="spotlight-bg border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-24">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">For Buyers & Commissioners</p>
            <h1 className="mt-3 font-display text-5xl sm:text-7xl text-white neon-red max-w-3xl">Put L.A. on your slate.</h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-[#181824]/70 p-8 sm:p-10" data-testid="license-card">
              <Building2 size={30} className="text-[#E11D48]" />
              <h2 className="mt-4 font-display text-3xl sm:text-4xl text-white">License the Original Season</h2>
              <p className="mt-4 text-gray-300 leading-relaxed">
                The completed original season of Laugh at L.A. — hybrid comedy variety, approx. 22-minute standalone episodes, hosted by Todd Bridges. Stand-up plays to a live audience; scripted sketches air between sets. Think Def Comedy Jam for the room and Chappelle's Show for the sketch sensibility.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="/assets/docs/laugh-at-deck.pdf" download data-testid="download-deck"
                  className="inline-flex items-center gap-2 rounded-full bg-[#F59E0B] px-6 py-3 font-bold text-black transition-transform hover:scale-105">
                  <Download size={16} /> Download the Deck
                </a>
                <a href="/assets/docs/buyer-one-sheet.pdf" download data-testid="download-one-sheet"
                  className="inline-flex items-center gap-2 rounded-full border border-[#F59E0B]/60 px-6 py-3 font-semibold text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors">
                  <Download size={16} /> Download the Buyer One-Sheet
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-white/10 bg-[#181824]/70 p-8 sm:p-10" data-testid="commission-card">
              <RefreshCw size={30} className="text-[#F59E0B]" />
              <h2 className="mt-4 font-display text-3xl sm:text-4xl text-white">Commission a New Edition</h2>
              <p className="mt-4 text-gray-300 leading-relaxed">
                The Laugh @ format is built to travel: local comics and writers, the city's landmarks, a regional premiere. Talk to Providence Film Group about an updated edition or a local adaptation — Laugh @ Atlanta, New York, Chicago, Miami, and beyond.
              </p>
              <a href={`mailto:${SOCIAL.emailPfg}?subject=Licensing%20inquiry%20—%20Laugh%20at%20L.A.`} data-testid="licensing-inquiry-link"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 font-semibold text-white hover:border-[#F59E0B] hover:text-[#F59E0B] transition-colors">
                <Mail size={16} /> {SOCIAL.emailPfg}
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="mt-6">
          <div className="rounded-2xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#181824] to-[#0A0A0E] p-8 sm:p-10" data-testid="screener-card">
            <div className="flex items-center gap-3">
              <KeyRound size={26} className="text-[#F59E0B]" />
              <h2 className="font-display text-3xl sm:text-4xl text-white">Request Screener Access</h2>
            </div>
            <p className="mt-3 max-w-2xl text-gray-300 leading-relaxed">
              Screeners of the original season are available to qualified buyers and commissioners. Tell us who you are and we'll issue viewing credentials. This route is separate from the fan mailing list.
            </p>
            <ScreenerForm />
          </div>
        </Reveal>
      </section>
    </main>
  );
}
